import { useCallback, useRef, useState } from 'react';

import { MFA_OUTCOME } from '../constants';
import { outcomeForStatus, readMfaError } from '../helpers';

/**
 * Drives the TOTP MFA challenge for an MFA-gated operation, following the
 * "Native SSH - MFA" contract: the first call is made without a code; a 428
 * response opens the TOTP modal; the operation is retried with the entered code
 * via the code in the request body until it succeeds, is rejected, or is cancelled.
 *
 * This is the engine behind MfaProvider — components should not call it
 * directly; use the useMfa() hook to trigger the app-level prompt instead. The
 * provider owns a single instance: it feeds `run` to context and renders the
 * one <MfaModal {...modalProps} />.
 *
 * run() accepts an optional second argument `{ onMfaRequired }`. When a 428 is
 * detected, `onMfaRequired()` fires before the modal opens — callers typically
 * use this to clear their button loading state so the spinner doesn't persist
 * behind the MFA modal.
 *
 * @returns {{ run: Function, modalProps: Object }}
 */
const useMfaVerification = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorCode, setErrorCode] = useState(null);

  // Holds the in-flight challenge: the operation to retry and the resolver of
  // the promise returned by run().
  const pendingRef = useRef(null);

  const settle = useCallback((response) => {
    const pending = pendingRef.current;

    pendingRef.current = null;
    setIsOpen(false);
    setIsVerifying(false);
    setErrorCode(null);
    if (pending) pending.resolve(response);
  }, []);

  const run = useCallback(async (operation, { onMfaRequired } = {}) => {
    const response = await operation();

    if (outcomeForStatus(response?.status) !== MFA_OUTCOME.REQUIRED) {
      return response;
    }

    // 428 MFA_REQUIRED — notify the caller (e.g. to clear a button loading
    // state) before the modal opens, then prompt for a code and retry.
    onMfaRequired?.();

    return new Promise((resolve) => {
      pendingRef.current = { operation, resolve };
      setErrorCode(null);
      setIsVerifying(false);
      setIsOpen(true);
    });
  }, []);

  const onSubmit = useCallback(
    async (code) => {
      const pending = pendingRef.current;

      if (!pending) return;

      setIsVerifying(true);
      setErrorCode(null);

      let response;

      try {
        response = await pending.operation(code);
      } catch {
        // Network / unexpected failure — treat as a retryable generic error.
        setIsVerifying(false);
        setErrorCode(null);

        return;
      }

      const outcome = outcomeForStatus(response?.status);

      if (outcome === MFA_OUTCOME.SUCCESS) {
        settle(response);

        return;
      }

      // Any non-success outcome (INVALID, TERMINAL, PASSTHROUGH, etc.) —
      // keep the modal open and show the error so the user can retry or
      // dismiss manually.
      const errorFromBody = await readMfaError(response);

      setIsVerifying(false);
      setErrorCode(errorFromBody);
    },
    [settle]
  );

  const onClose = useCallback(() => settle(null), [settle]);

  return {
    run,
    modalProps: { isOpen, isVerifying, errorCode, onSubmit, onClose },
  };
};

export default useMfaVerification;
